import { Component, Vue, Watch } from "vue-property-decorator";
import { format, parse, parseISO } from "date-fns";
import Loader from "@/components/Includes/Loader/Loader";
import TableEmpty from "@/components/Includes/Table/TableFooter/TableEmpty/TableEmpty";
import Pagination from "@/components/Includes/Pagination/Pagination";
import { namespace } from "vuex-class";
import IPartner from "@/interfaces/IPartner";
import ITableCol from "@/interfaces/ITableCol";
import getTableColumns from "@/libs/getTableColumns";

const parnerModule = namespace("partner");

@Component({
  components: {
    Loader,
    TableEmpty,
    Pagination,
  },
})
export default class Partners extends Vue {
  @parnerModule.Action("fetchPartners")
  private readonly fetchPartnersAction!: (params: {
    query: string;
  }) => Promise<{ links: number; data: IPartner[] }>;

  @parnerModule.Action("fetchPartner")
  private readonly fetchPartnerAction!: (payload: {
    id: number;
  }) => Promise<IPartner>;

  @parnerModule.Action("createPartner")
  private readonly createPartnerAction!: (payload: {
    name: string;
  }) => IPartner;

  @parnerModule.Action("updatePartner")
  private readonly updatePartnerAction!: (payload: {
    name: string;
    id: number;
  }) => IPartner;

  private readonly limit = 100;
  private page = 1;
  private isLoading = false;
  private totalItems = 0;
  private partnerPromoCodes: IPartner[] = [];

  private isShowCreateModal = false;
  private isShowUpdateModal = false;

  private formData: { name: string; id?: number } | null = null;

  private get promoApiHost() {
    if (process.env.NODE_ENV === 'production') {
      return 'switcherrypromo-api.com';
    }

    return 'dev.switcherrypromo-api.com';
  }

  private get fields(): ITableCol[] {
    const columns = getTableColumns(this.partnerPromoCodes, false);

    return Array.isArray(columns) ? columns : [];
  }

  private formatDate(date: string) {
    return format(parseISO(date), "dd.MM.yyyy HH:mm:ss");
  }

  private async onRowClicked({ id }: IPartner) {
    this.isShowUpdateModal = true;
    const { name } = await this.fetchPartnerAction({ id });

    this.formData = { name, id };
  }

  private async fetchPartners() {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;
    try {
      const { data, links } = await this.fetchPartnersAction({
        query: `?pagination=true&page=${this.page}&limit=${this.limit}`,
      });

      this.totalItems = links;
      this.partnerPromoCodes = data;
    } catch {
      this.partnerPromoCodes = [];
      this.totalItems = 0;
      this.page = 1;
    }

    this.isLoading = false;
  }

  private async createPartner() {
    if (!this.formData || this.isLoading) {
      return;
    }

    this.isLoading = true;

    try {
      await this.createPartnerAction({
        name: this.formData.name,
      });
      this.isShowCreateModal = false;
      this.isLoading = false;
      this.fetchPartners();
    } catch (error) {
      console.error(error);
      this.isLoading = false;
    }
  }

  private async updatePartner() {
    if (!this.formData || !this.formData.id || this.isLoading) {
      return;
    }

    this.isLoading = true;

    try {
      await this.updatePartnerAction({
        id: this.formData.id,
        name: this.formData.name,
      });

      this.isShowUpdateModal = false;
      this.isLoading = false;
      this.fetchPartners();
    } catch (error) {
      console.error(error);
      this.isLoading = false;
    }
  }

  @Watch("isShowCreateModal")
  private onChangeIsShowCreateModal() {
    if (this.isShowCreateModal) {
      this.formData = {
        name: "",
      };
    }
  }

  @Watch("page")
  private onChangePage() {
    this.fetchPartners();
  }

  private mounted() {
    this.fetchPartners();
  }
}
